import { StateService } from '@infra/state/state.service';
import { AsyncPipe, NgClass, UpperCasePipe } from '@angular/common';
import { Component, inject, Input, type OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CustomerDTO, RolodexCompanyDTO } from '@api/models';
import {
  NgbDropdown,
  NgbDropdownButtonItem,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import { KeyboardClickableDirective } from '@common/directives/keyboard-clickable.directive';
import { ResourcePipe } from '@shared/pipes/resource.pipe';
import { AuthService } from '@infra/auth/auth.service';
import { toInitials } from '@common/functions/to-initials';

enum ParticipantType {
  ACCOUNT = 'account',
  BOOKING_AGENT = 'booking-agent',
  CUSTOMER = 'customer',
}

@Component({
  selector: 'app-assignment-participants-section',
  templateUrl: './assignment-participants-section.component.html',
  imports: [
    KeyboardClickableDirective,
    NgClass,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    UpperCasePipe,
    ResourcePipe,
    AsyncPipe,
  ],
})
export class AssignmentParticipantsSectionComponent implements OnChanges {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly state = inject(StateService);

  @Input() selectedCustomer: CustomerDTO;
  @Input() selectedBookingAgent: RolodexCompanyDTO;
  @Input() selectedAccount: RolodexCompanyDTO;

  participants: {
    participant: RolodexCompanyDTO | CustomerDTO;
    type: 'account' | 'booking-agent' | 'customer';
    colorSchema: 'blue' | 'yellow' | 'lavender';
    name: string;
    salutationTypeId: string;
    initials: string;
  }[] = [];

  ngOnChanges(): void {
    this.showParticipants();
  }

  viewParticipant(participant: RolodexCompanyDTO | CustomerDTO, type: string) {
    this.state.showSearchPopup(false);
    this.state.setAssignment(null);

    switch (type) {
      case ParticipantType.ACCOUNT:
        this.router.navigate([`/${this.authService.companyUrl}/accounts/${participant.id}`]);
        this.state.setAccount(this.selectedAccount);
        break;
      case ParticipantType.BOOKING_AGENT:
        this.router.navigate([`/${this.authService.companyUrl}/partners/${participant.id}`]);
        this.state.setBookingAgent(this.selectedBookingAgent);
        break;
      case ParticipantType.CUSTOMER:
        this.router.navigate([`/${this.authService.companyUrl}/customers/${participant.id}`]);
        this.state.setCustomer(this.selectedCustomer);
        break;
    }
  }

  private showParticipants(): void {
    const participants = [];
    if (this.selectedAccount) {
      participants.push({
        participant: this.selectedAccount,
        type: ParticipantType.ACCOUNT,
        colorSchema: 'lavender',
        name: this.selectedAccount.name,
        initials: toInitials(this.selectedAccount.name),
      });
    }

    if (this.selectedBookingAgent && this.selectedBookingAgent.id != this.authService.companyId) {
      participants.push({
        participant: this.selectedBookingAgent,
        type: ParticipantType.BOOKING_AGENT,
        colorSchema: 'blue',
        name: this.selectedBookingAgent.name,
        initials: toInitials(this.selectedBookingAgent.name),
      });
    }

    if (this.selectedCustomer) {
      if (this.selectedCustomer.defaultContact) {
        participants.push({
          participant: this.selectedCustomer,
          type: ParticipantType.CUSTOMER,
          colorSchema: 'yellow',
          name: this.selectedCustomer.defaultContact.fullName,
          salutationTypeId: this.selectedCustomer.defaultContact.salutationTypeId,
          initials: toInitials(this.selectedCustomer.defaultContact.fullName),
        });
      }
    }

    if (this.selectedBookingAgent && participants.length === 0) {
      participants.push({
        participant: this.selectedBookingAgent,
        type: ParticipantType.BOOKING_AGENT,
        colorSchema: 'blue',
        name: this.selectedBookingAgent.name,
        initials: toInitials(this.selectedBookingAgent.name),
      });
    }

    this.participants = participants;
  }
}
